import pmCommitmentService from '@/services/pmCommitmentService';
import pmConstraintService from '@/services/pmConstraintService';
import pmDecisionService from '@/services/pmDecisionService';
import pmMilestoneService from '@/services/pmMilestoneService';
import { resolveCurrentUserId } from '@/utils/resolveCurrentUserId';
import { buildUserProjectsOverview } from '@/utils/userProjectsOverview';

const EMPTY_OVERVIEW = () => ({
  totals: {
    projectCount: 0,
    myOpenCount: 0,
    myOverdueCount: 0,
    myDueSoonCount: 0,
    projectOpenCount: 0,
    projectOverdueCount: 0,
    internalOpenCount: 0,
    crossOpenCount: 0,
    openDecisionsCount: 0,
    overdueDecisionsCount: 0,
    completedMilestonesCount: 0,
    totalMilestonesCount: 0,
    overdueMilestonesCount: 0,
  },
  projectSummaries: [],
  upcomingCommitments: [],
  myOpenCommitments: [],
  activeConstraints: [],
  nextRequiredDecision: null,
  milestones: [],
  nextMilestone: null,
});

export default {
  namespaced: true,

  state: {
    commitments: [],
    total: 0,
    projectId: null,
    loading: false,
    error: null,
    myCommitments: [],
    myCommitmentsTotal: 0,
    myCommitmentsLoading: false,
    myCommitmentsError: null,
    overview: EMPTY_OVERVIEW(),
    overviewLoading: false,
    overviewError: null,
  },

  getters: {
    commitments: (state) => state.commitments,
    total: (state) => state.total,
    projectId: (state) => state.projectId,
    loading: (state) => state.loading,
    error: (state) => state.error,
    isEmpty: (state) => !state.loading && !state.error && state.commitments.length === 0,
    hasProjectContext: (state) => Boolean(state.projectId),
    myCommitments: (state) => state.myCommitments,
    myCommitmentsLoading: (state) => state.myCommitmentsLoading,
    myCommitmentsError: (state) => state.myCommitmentsError,
    myCommitmentsEmpty: (state) => (
      !state.myCommitmentsLoading
      && !state.myCommitmentsError
      && state.myCommitments.length === 0
    ),
    overview: (state) => state.overview,
    overviewLoading: (state) => state.overviewLoading,
    overviewError: (state) => state.overviewError,
    overviewEmpty: (state, getters, rootState, rootGetters) => {
      const hasContext = Boolean(rootGetters['pmProject/selectedProjectId']);
      return (
        !state.overviewLoading
        && !state.overviewError
        && (!hasContext || (state.overview?.totals?.projectCount ?? 0) === 0)
      );
    },
  },

  mutations: {
    SET_PROJECT_ID(state, projectId) {
      state.projectId = projectId;
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_COMMITMENTS(state, { items, total }) {
      state.commitments = items || [];
      state.total = typeof total === 'number' ? total : state.commitments.length;
    },
    ADD_COMMITMENT(state, commitment) {
      state.commitments.unshift(commitment);
      state.total += 1;
    },
    UPDATE_COMMITMENT(state, updated) {
      const index = state.commitments.findIndex((c) => c.id === updated.id);
      if (index !== -1) {
        state.commitments.splice(index, 1, updated);
      }
    },
    REMOVE_COMMITMENT(state, id) {
      state.commitments = state.commitments.filter((c) => c.id !== id);
      state.total = Math.max(0, state.total - 1);
    },
    RESET(state) {
      state.commitments = [];
      state.total = 0;
      state.projectId = null;
      state.loading = false;
      state.error = null;
      state.myCommitments = [];
      state.myCommitmentsTotal = 0;
      state.myCommitmentsLoading = false;
      state.myCommitmentsError = null;
      state.overview = EMPTY_OVERVIEW();
      state.overviewLoading = false;
      state.overviewError = null;
    },
    SET_MY_COMMITMENTS_LOADING(state, loading) {
      state.myCommitmentsLoading = loading;
    },
    SET_MY_COMMITMENTS_ERROR(state, error) {
      state.myCommitmentsError = error;
    },
    SET_MY_COMMITMENTS(state, { items, total }) {
      state.myCommitments = items || [];
      state.myCommitmentsTotal = typeof total === 'number' ? total : state.myCommitments.length;
    },
    SET_OVERVIEW_LOADING(state, loading) {
      state.overviewLoading = loading;
    },
    SET_OVERVIEW_ERROR(state, error) {
      state.overviewError = error;
    },
    SET_OVERVIEW(state, overview) {
      state.overview = overview || EMPTY_OVERVIEW();
    },
  },

  actions: {
    setProjectContext({ commit, dispatch }, projectId) {
      commit('SET_PROJECT_ID', projectId);
      if (projectId) {
        return dispatch('fetchCommitments');
      }
      commit('SET_COMMITMENTS', { items: [], total: 0 });
      return Promise.resolve();
    },

    async fetchCommitments({ commit, state }) {
      if (!state.projectId) {
        commit('SET_COMMITMENTS', { items: [], total: 0 });
        return;
      }
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const data = await pmCommitmentService.listCommitments(state.projectId);
        commit('SET_COMMITMENTS', {
          items: data?.items || [],
          total: data?.total ?? 0,
        });
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to load commitments';
        commit('SET_ERROR', message);
        commit('SET_COMMITMENTS', { items: [], total: 0 });
        throw error;
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async createCommitment({ commit, state }, payload) {
      commit('SET_ERROR', null);
      try {
        const commitment = await pmCommitmentService.createCommitment(state.projectId, payload);
        commit('ADD_COMMITMENT', commitment);
        return commitment;
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to create commitment';
        commit('SET_ERROR', message);
        throw error;
      }
    },

    async updateCommitment({ commit, state }, { id, payload }) {
      commit('SET_ERROR', null);
      try {
        const commitment = await pmCommitmentService.updateCommitment(
          state.projectId,
          id,
          payload,
        );
        commit('UPDATE_COMMITMENT', commitment);
        return commitment;
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to update commitment';
        commit('SET_ERROR', message);
        throw error;
      }
    },

    async deleteCommitment({ commit, state }, id) {
      commit('SET_ERROR', null);
      try {
        await pmCommitmentService.deleteCommitment(state.projectId, id);
        commit('REMOVE_COMMITMENT', id);
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to delete commitment';
        commit('SET_ERROR', message);
        throw error;
      }
    },

    async updateCommitmentStatus({ commit, state }, { id, status, completionComment }) {
      commit('SET_ERROR', null);
      try {
        const commitment = await pmCommitmentService.updateStatus(
          state.projectId,
          id,
          status,
          { completionComment },
        );
        commit('UPDATE_COMMITMENT', commitment);
        return commitment;
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to update status';
        commit('SET_ERROR', message);
        throw error;
      }
    },

    async recordCause({ commit, state }, { id, payload }) {
      commit('SET_ERROR', null);
      try {
        const commitment = await pmCommitmentService.recordCause(state.projectId, id, payload);
        commit('UPDATE_COMMITMENT', commitment);
        return commitment;
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to record cause';
        commit('SET_ERROR', message);
        throw error;
      }
    },

    async fetchMyCommitmentsAcrossProjects({ commit, dispatch, rootGetters }) {
      commit('SET_MY_COMMITMENTS_LOADING', true);
      commit('SET_MY_COMMITMENTS_ERROR', null);
      try {
        await dispatch('pmProject/fetchProjects', null, { root: true });
        const userId = resolveCurrentUserId(rootGetters.getUser);
        if (!userId) {
          throw new Error('Could not resolve the current user id');
        }

        const projects = rootGetters['pmProject/activeProjects'] || [];
        const failures = [];

        const perProject = await Promise.all(
          projects.map(async (project) => {
            try {
              const data = await pmCommitmentService.listCommitments(project.id, { status: 'open' });
              return (data?.items || [])
                .filter((item) => String(item.ownerUserId) === userId && item.status === 'open')
                .map((item) => ({
                  ...item,
                  projectId: project.id,
                  projectName: project.name,
                }));
            } catch (error) {
              const message = error.response?.data?.message || error.message || 'Failed to load commitments';
              failures.push(`${project.name}: ${message}`);
              return [];
            }
          }),
        );

        const items = perProject.flat();
        if (failures.length > 0 && items.length === 0) {
          throw new Error(failures.join('; '));
        }
        if (failures.length > 0) {
          commit('SET_MY_COMMITMENTS_ERROR', failures.join('; '));
        }

        commit('SET_MY_COMMITMENTS', { items, total: items.length });
        return items;
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to load my commitments';
        commit('SET_MY_COMMITMENTS_ERROR', message);
        commit('SET_MY_COMMITMENTS', { items: [], total: 0 });
        throw error;
      } finally {
        commit('SET_MY_COMMITMENTS_LOADING', false);
      }
    },

    async fetchMyOpenCommitmentsForContext(ctx) {
      return ctx.dispatch('fetchMyCommitmentsForContext', { status: 'open' });
    },

    async fetchMyCommitmentsForContext({ commit, dispatch, rootGetters }, { status = 'open' } = {}) {
      commit('SET_MY_COMMITMENTS_LOADING', true);
      commit('SET_MY_COMMITMENTS_ERROR', null);
      try {
        const projectId = rootGetters['pmProject/selectedProjectId'];
        const project = rootGetters['pmProject/selectedProject'];
        if (!projectId) {
          commit('SET_MY_COMMITMENTS', { items: [], total: 0 });
          return [];
        }

        const userId = resolveCurrentUserId(rootGetters.getUser);
        if (!userId) {
          throw new Error('Could not resolve the current user id');
        }

        await dispatch('setProjectContext', projectId);
        const apiStatus = status && status !== 'all' ? { status } : {};
        const data = await pmCommitmentService.listCommitments(projectId, apiStatus);
        const items = (data?.items || [])
          .filter((item) => String(item.ownerUserId) === userId)
          .map((item) => ({
            ...item,
            projectId,
            projectName: project?.name || '',
          }));

        commit('SET_MY_COMMITMENTS', { items, total: items.length });
        return items;
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to load my commitments';
        commit('SET_MY_COMMITMENTS_ERROR', message);
        commit('SET_MY_COMMITMENTS', { items: [], total: 0 });
        throw error;
      } finally {
        commit('SET_MY_COMMITMENTS_LOADING', false);
      }
    },

    async fetchUserProjectsOverview({ commit, rootGetters }) {
      commit('SET_OVERVIEW_LOADING', true);
      commit('SET_OVERVIEW_ERROR', null);
      try {
        const projectId = rootGetters['pmProject/selectedProjectId'];
        const project = rootGetters['pmProject/selectedProject'];
        const userId = resolveCurrentUserId(rootGetters.getUser);

        if (!userId) {
          throw new Error('Could not resolve the current user id');
        }

        if (!projectId || !project) {
          commit('SET_OVERVIEW', EMPTY_OVERVIEW());
          return EMPTY_OVERVIEW();
        }

        const [commitmentsData, constraintsData, decisionsData, milestonesData] = await Promise.all([
          pmCommitmentService.listCommitments(projectId, { status: 'open' }),
          pmConstraintService.listConstraints(projectId),
          pmDecisionService.listDecisions(projectId),
          pmMilestoneService.listMilestones(projectId),
        ]);
        const overview = buildUserProjectsOverview({
          projects: [project],
          commitmentsByProject: {
            [String(projectId)]: commitmentsData?.items || [],
          },
          constraintsByProject: {
            [String(projectId)]: constraintsData?.items || [],
          },
          decisionsByProject: {
            [String(projectId)]: decisionsData?.items || [],
          },
          milestonesByProject: {
            [String(projectId)]: milestonesData?.items || [],
          },
          userId,
        });

        commit('SET_OVERVIEW', overview);
        return overview;
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to load overview';
        commit('SET_OVERVIEW_ERROR', message);
        commit('SET_OVERVIEW', EMPTY_OVERVIEW());
        throw error;
      } finally {
        commit('SET_OVERVIEW_LOADING', false);
      }
    },
  },
};
